import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarDays,
  Check,
  DoorOpen,
  Grid3x3,
  Loader2,
  LogOut,
  Mic,
  MicOff,
  Moon,
  PauseCircle,
  RefreshCw,
  TriangleAlert,
  User,
} from 'lucide-react';
import { Link, createRoute, redirect } from '@tanstack/react-router';
import { rootRoute } from './root';
import { authClient, getSession, devSignOut, DEV_AUTH_BYPASS } from '@/lib/auth-client';
import { startRumForUser, stopRum, rumAction, logCall, reportRenderError } from '@/lib/rum';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useCockpit, type HangupSource } from '@/cockpit/useCockpit';
import { isEditableTarget, resolveHangupShortcut } from '@/cockpit/hangup-shortcuts';
import { useTwilioPhone } from '@/cockpit/useTwilioPhone';
import { PresenceOverlay } from '@/cockpit/PresenceOverlay';
import { playPresenceChime } from '@/cockpit/chime';
import { useTitleAlert } from '@/cockpit/useTitleAlert';
import { useFocusOnMount } from '@/cockpit/useFocusOnMount';
import { TwilioControlBox } from '@/cockpit/TwilioControlBox';
import { useIdleMicToast } from '@/cockpit/useIdleMicToast';
import { reportMicBlocked, reportMicRecovered } from '@/cockpit/audio-report';
import { micEpisode } from '@/cockpit/mic-health';
import type { MicState } from '@/cockpit/mic-health';
import { MicCheckDialog, type MicCheckPhone } from '@/cockpit/MicCheckDialog';
import { hasMicPass, writePassedDay } from '@/cockpit/mic-check';
import { MANUAL_TABS_CHANNEL, useOpenTabCount } from '@/cockpit/useOpenTabCount';
import { poolName, type CockpitState } from '@/cockpit/state';
import { decideInbound } from '@/cockpit/inbound-guard';
import { isPoolJoinable } from '@/cockpit/protocol';
import type { LeadCall, OffHours, PoolInfo } from '@/cockpit/protocol';
import {
  poolPauseBanner,
  evictionWarning,
  offHoursSummary,
  unavailableReasonLabel,
  poolCardState,
} from '@/cockpit/off-hours';
import { leaveStalled, formatWaitedFor } from '@/cockpit/drain';
import { WAITING_TIPS, TIP_ROTATION_MS, nextTipIndex } from '@/cockpit/tips';
import { useBffPing } from '@/useBffPing';
import { useStaleBundle } from '@/lib/useStaleBundle';
import { refreshHint } from '@/lib/version-check';
import { TalkTrackPanel, LEADFLOW_RESOURCES_URL } from '@/components/cockpit/talk-track';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Boundary } from '@/components/boundary';
import { ReloadGuard } from '@/components/error-state';
import { CallSurfaceError } from '@/components/route-error';
import { EmailCheck } from '@/cockpit/EmailCheck';
import {
  SellerCalendar,
  CalendarUnavailable,
  slotCountBadge,
} from '@/components/cockpit/seller-calendar';

// Dev-only SDR impersonation. A `?sdr=<name>` page param makes this tab connect as a
// DISTINCT SDR (the BFF honors it only when auth is off — see app.ts WS open), so you
// can open several tabs as different SDRs in one pool and watch the events line up.
// The URL is the source of truth (shareable + survives reload); switching reloads the
// page so the WS cleanly reconnects as the new SDR. Default shared dev SDR shown as a
// placeholder hint (mirrors the BFF's `Cockpit SDR` fallback).
const DEV_SDR_PLACEHOLDER = 'Cockpit SDR';

function getImpersonateSdr(): string | null {
  if (!import.meta.env.DEV) return null;
  const v = new URLSearchParams(window.location.search).get('sdr');
  return v && v.trim() ? v.trim() : null;
}

function setImpersonateSdr(name: string): void {
  const url = new URL(window.location.href);
  const v = name.trim();
  if (v) url.searchParams.set('sdr', v);
  else url.searchParams.delete('sdr');
  window.location.href = url.toString();
}

// Lifecycle badge styling for a pool in the picker. `open` is the default joinable
// state and needs no badge; `calling` (also joinable) gets a live green badge so the
// SDR sees it's actively dialing; the rest tell the SDR why a pool may be unavailable.
const POOL_STATUS_BADGE: Record<string, { label: string; className: string }> = {
  calling: { label: 'calling', className: 'bg-green-100 text-green-700' },
  paused: { label: 'paused', className: 'bg-amber-100 text-amber-700' },
  draining: { label: 'draining', className: 'bg-blue-100 text-blue-700' },
  draft: { label: 'draft', className: 'bg-gray-200 text-gray-600' },
  closed: { label: 'closed', className: 'bg-gray-200 text-gray-600' },
  // Reported by the core in place of open/paused when every dialable lead is asleep.
  // It is not in JOINABLE_POOL_STATES, so the picker refuses the join for the same
  // reason it refuses `closed`.
  off_hours: { label: 'off hours', className: 'bg-amber-100 text-amber-700' },
};

// The two on-call fallbacks. Neither says what broke: neither reader can act on it.

// The call is untouched, but someone who cannot find mute must not be left
// assuming they are muted.
function ControlsUnavailable() {
  return (
    <p
      role="alert"
      className="border-destructive/40 bg-destructive/5 text-destructive px-4 py-2 text-sm"
    >
      Mute and the device pickers are unavailable. Your call is unaffected — assume your microphone
      is live.
    </p>
  );
}

// The countdown runs either way, so this carries the confirm or the seat is
// lost to a formatting bug.
function PresenceFallback({ online, onConfirm }: { online: boolean; onConfirm: () => void }) {
  return (
    <div role="alert" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-950">
        Are you still there? Confirm to stay in the pool.
      </p>
      <button
        type="button"
        onClick={onConfirm}
        disabled={!online}
        className="mt-3 w-full rounded-md bg-amber-700 px-4 py-2.5 text-base font-semibold text-white hover:bg-amber-800 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {online ? 'I’m here' : 'Reconnecting…'}
      </button>
    </div>
  );
}

// In-pool banner explaining an off-hours wait: the pool is open and healthy, but
// every remaining lead is asleep. Shows the asleep-lead count, the pool's timezone
// makeup (a bar per zone), and when the earliest zone wakes.
function OffHoursBanner({ offHours }: { offHours: OffHours }) {
  const { count, timezones, resting = 0 } = offHours;
  const maxCount = timezones.reduce((m, z) => Math.max(m, z.count), 0);
  const warning = evictionWarning(offHours);

  return (
    <div
      className="mb-5 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
      data-testid="off-hours-banner"
      role="status"
    >
      <div className="flex items-center gap-2 font-medium">
        <Moon className="h-4 w-4 shrink-0" />
        <span>Dialing paused — nothing is broken</span>
      </div>
      {warning && (
        <p className="mt-1 font-semibold text-amber-900" data-testid="off-hours-evict-warning">
          {warning}
        </p>
      )}
      <p className="mt-1 text-amber-700">
        You’re outside your leads’ local calling hours. {count.toLocaleString()} lead
        {count === 1 ? '' : 's'} waiting for their local calling window — dialing resumes
        automatically.
      </p>
      {timezones.length > 0 && (
        <ul className="mt-3 space-y-1">
          {timezones.map((z) => (
            <li key={z.timezone} className="flex items-center gap-2" data-testid="off-hours-zone">
              <span className="w-32 shrink-0 truncate">{z.label}</span>
              <span className="h-2 flex-1 rounded bg-amber-200">
                <span
                  className="block h-2 rounded bg-amber-500"
                  style={{ width: `${maxCount ? Math.round((z.count / maxCount) * 100) : 0}%` }}
                />
              </span>
              <span className="w-24 shrink-0 text-right tabular-nums">
                {z.count.toLocaleString()} {z.timezone === 'unknown' ? '· tz unknown' : '· 💤'}
              </span>
            </li>
          ))}
        </ul>
      )}
      {resting > 0 && (
        <p className="mt-2 text-amber-700" data-testid="off-hours-resting">
          A further {resting.toLocaleString()} lead{resting === 1 ? ' is' : 's are'} awake but were
          called recently.
        </p>
      )}
      {/* The soonest RETURN, which is not always the soonest window: leads resting on
          their ring budget can come back first. `offHoursSummary` picks between them
          from the instant the core chose, so both halves of the banner agree. */}
      <p className="mt-2 text-amber-700">Next: {offHoursSummary({ offHours })}</p>
    </div>
  );
}

// Why the SDR is back on the picker, when the seat was taken rather than left. All
// three clocks removed them for the same failure to answer, so the messages end the
// same way.
function exitReasonMessage(exitReason: 'idle' | 'cap' | 'silence_gate' | null): string | null {
  switch (exitReason) {
    case 'cap':
      return 'You were taken out of your pool because the “still there?” prompt went unanswered. Join again to carry on.';
    case 'idle':
      return 'You were taken out of your pool after a few minutes with no activity. Join again to carry on.';
    case 'silence_gate':
      return 'You were taken out of your pool because the silence prompt on your last call went unanswered. Join again to carry on.';
    default:
      return null;
  }
}

// The check records through the browser, not through the softphone, so a missing
// voice token is no reason to withhold it — an SDR whose device never registered
// still has a microphone, and may well be here BECAUSE something is wrong. Only a
// mic already known to be failing sends them elsewhere: the banner's re-check is
// the tool for that, and this would just fail the same way with a second voice.
const micCheckBlocked = (mic: MicState): boolean => mic === 'warn' || mic === 'blocked';

// A disabled control that says nothing about why reads as broken.
function micCheckHint(busy: boolean, mic: MicState): string | undefined {
  if (busy) return 'Not while you’re joining a pool';
  if (micCheckBlocked(mic)) return 'Fix the microphone problem above first';
  return undefined;
}

function PoolPicker({
  pools,
  busy,
  pendingName,
  micBlocked,
  phone,
  exitReason,
  error,
  onSelect,
}: {
  pools: PoolInfo[];
  busy: boolean;
  pendingName: string;
  // The mic check lives here rather than in the softphone panel: the picker only
  // exists between pools, so "not while you're dialing" needs no flag to enforce.
  phone: MicCheckPhone;
  // No mic, long enough that we stopped expecting one back. Joining would put this
  // SDR on live calls nobody can hear them on — the banner above says why.
  micBlocked: boolean;
  // Set when the SDR was taken out of a pool rather than leaving it, so the picker can
  // say so — arriving here unexplained reads as a bug.
  exitReason: 'idle' | 'cap' | 'silence_gate' | null;
  // Why the last join attempt bounced them back here, when the core refused it.
  error: string | null;
  onSelect: (poolId: string) => void;
}) {
  // Clicking a card no longer joins immediately — it stages the pool for a
  // confirmation step so an accidental click can't silently start dialing real
  // people. The SDR has to press "Start dialing" before we send the join frame.
  const [confirmPoolId, setConfirmPoolId] = useState<string | null>(null);
  const confirmPool = pools.find((p) => p.id === confirmPoolId) ?? null;
  // Re-derived every render from the polled list, so it catches a pool that stopped
  // being joinable after it was staged. Null while the pool is still fine.
  const stagedReason =
    confirmPool && !isPoolJoinable(confirmPool.status)
      ? (unavailableReasonLabel(confirmPool) ?? `This pool is ${confirmPool.status}`)
      : null;

  // The countdown renders from an absolute instant, so it needs a clock of its own to
  // move. 30s against a minute-granular label bounds the lag at half a minute.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const [micCheckOpen, setMicCheckOpen] = useState(false);
  // Read on mount rather than on a clock: the join itself re-reads, so a pass that
  // expires at midnight is caught where it matters.
  const [checkedMic, setCheckedMic] = useState(() => hasMicPass());
  // A pass made in another tab has to unlock this one: with every card disabled, the
  // join-time re-read is never reached.
  useEffect(() => {
    const onStorage = (): void => setCheckedMic(hasMicPass());
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  const micCheckWhyNot = micCheckHint(busy, phone.micState);
  const micCheckDisabled = micCheckWhyNot !== undefined;

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Choose a pool</h2>
          <p className="text-muted-foreground text-sm">
            {busy ? `Joining ${pendingName}…` : 'Pick a pool to start receiving calls.'}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          {checkedMic && (
            <Button
              variant="outline"
              size="sm"
              data-testid="mic-check-button"
              disabled={micCheckDisabled}
              title={micCheckWhyNot}
              onClick={() => setMicCheckOpen(true)}
            >
              <Check className="text-emerald-600" />
              Mic checked this week
            </Button>
          )}
          {/* The manual dialer has no nav of its own; this is the one way in from the
              cockpit. Plain link: the page itself says when the dialer is switched off. */}
          <Button variant="outline" size="sm" asChild>
            <Link to="/dial" data-testid="manual-dialer-link">
              <Grid3x3 />
              Manual dialer
            </Link>
          </Button>
        </div>
      </div>
      <MicCheckDialog
        open={micCheckOpen}
        onClose={() => setMicCheckOpen(false)}
        onAllGood={() => {
          writePassedDay(new Date());
          setCheckedMic(true);
        }}
        phone={phone}
      />
      {(error ?? (busy ? null : exitReasonMessage(exitReason))) && (
        <p
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="pool-picker-error"
          role="status"
        >
          {/* A refused join is about the click they just made; a lost seat about the one
              before it. The fresher fact is the one worth reading. */}
          {error ?? exitReasonMessage(exitReason)}
        </p>
      )}
      {!checkedMic && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="mic-check-required"
        >
          <div>
            <p className="flex items-center gap-2 font-medium">
              <Mic className="size-4" />
              Check your microphone before you dial
            </p>
            {micCheckWhyNot && <p className="mt-0.5">{micCheckWhyNot}</p>}
          </div>
          <Button
            size="sm"
            data-testid="mic-check-button"
            disabled={micCheckDisabled}
            onClick={() => setMicCheckOpen(true)}
          >
            Check mic quality
          </Button>
        </div>
      )}
      {pools.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          You’re not assigned to any pool yet. Ask an admin to add you to a pool, then it’ll show up
          here.
        </p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {pools.map((pool) => {
            const { joinable, subline } = poolCardState(pool, {
              micBlocked,
              micUnchecked: !checkedMic,
              nowMs,
            });
            // Operator pause (status) wins over off-hours, matching the in-pool banner
            // precedence — off_hours data can linger while the pool is paused.
            const badge = POOL_STATUS_BADGE[pool.status];
            // An unavailable pool is stated and closed, not opened. It used to stay
            // clickable so its dialog could show a resume time; the card carries the
            // reason instead, so there is nothing behind the click worth a way in.
            const reason = unavailableReasonLabel(pool);
            const disabled = busy || !joinable;
            return (
              <button
                key={pool.id}
                type="button"
                data-testid="pool-card"
                disabled={disabled}
                onClick={() => setConfirmPoolId(pool.id)}
                style={{ borderLeftColor: pool.color }}
                className={cn(
                  'rounded-lg border border-l-4 p-4 text-left transition',
                  disabled ? 'opacity-50' : 'hover:bg-accent cursor-pointer',
                  !joinable && busy === false && 'cursor-not-allowed',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{pool.name}</div>
                  {badge && (
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[0.65rem] font-bold',
                        badge.className,
                      )}
                    >
                      {badge.label}
                    </span>
                  )}
                </div>
                {/* A greyed card with no reason is worse than no card. The pool's own
                    reason wins where it has one — it outlives the mic problem. No time
                    here on purpose: see unavailableReasonLabel. */}
                <div className="text-muted-foreground mt-1 text-xs">{reason ?? subline}</div>
                {(pool.clientNames?.length ?? 0) > 0 && (
                  <div className="text-muted-foreground mt-1 text-xs" data-testid="pool-clients">
                    {(pool.clientNames?.length ?? 0) > 1 ? 'Clients: ' : 'Client: '}
                    {pool.clientNames?.join(', ')}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Confirmation gate: selecting a card stages it here; the SDR must press
          "Start dialing" to actually join the pool and begin receiving calls.
          Guards against an accidental click immediately ringing real contacts. */}
      {confirmPool && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          data-testid="join-confirm-overlay"
          onClick={() => setConfirmPoolId(null)}
        >
          <div
            className="bg-card flex w-[min(28rem,94vw)] flex-col gap-4 rounded-lg border p-6 shadow-xl"
            data-testid="join-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              {stagedReason ? (
                <Moon className="h-5 w-5 text-amber-600" />
              ) : (
                <DoorOpen className="text-muted-foreground h-5 w-5" />
              )}
              <h2 className="text-lg font-semibold">
                {stagedReason
                  ? `${confirmPool.name} is no longer available`
                  : `Start dialing in ${confirmPool.name}?`}
              </h2>
            </div>
            <p className="text-muted-foreground text-sm">
              {stagedReason
                ? `${stagedReason}. Pick another pool.`
                : 'You’ll join this pool and start receiving live calls to real contacts right away. Make sure you’re ready before you start.'}
            </p>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setConfirmPoolId(null)}
                data-testid="join-confirm-cancel"
              >
                {stagedReason ? 'Close' : 'Cancel'}
              </Button>
              {/* Staging only proves the pool was joinable at CLICK time. The list is
                  polled and this modal re-derives from it, so the pool can finish its
                  pass or lose its window while the SDR reads — and the mic can die too.
                  Both have to be re-checked here or the button sends a join the core
                  will refuse. */}
              {!stagedReason && (
                <Button
                  disabled={micBlocked}
                  onClick={() => {
                    const id = confirmPool.id;
                    setConfirmPoolId(null);
                    // The banner that comes back is the explanation.
                    if (!hasMicPass()) {
                      setCheckedMic(false);
                      return;
                    }
                    onSelect(id);
                  }}
                  data-testid="join-confirm-start"
                >
                  {micBlocked ? 'Microphone unavailable' : 'Start dialing'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Rotates a talk-track tip every TIP_ROTATION_MS while the SDR waits for the
// next call, with a thin progress bar counting down to the next rotation.
// aria-live=polite so screen readers announce each new tip.
function RotatingTip() {
  const [i, setI] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (WAITING_TIPS.length <= 1) return;
    const t = setInterval(() => setI((n) => nextTipIndex(n, WAITING_TIPS.length)), TIP_ROTATION_MS);
    return () => clearInterval(t);
  }, []);

  // On each tip change snap the bar to 0 (no transition), then fill to 100% over
  // the rotation window — so it reads as a countdown to the next tip.
  useEffect(() => {
    setProgress(0);
    const r = requestAnimationFrame(() => setProgress(100));
    return () => cancelAnimationFrame(r);
  }, []);

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-2">
      <p
        className="text-muted-foreground text-sm italic"
        data-testid="waiting-tip"
        aria-live="polite"
      >
        💡 {WAITING_TIPS[i]}
      </p>
      <div className="bg-muted h-0.5 w-full overflow-hidden rounded" aria-hidden>
        <div
          className="bg-primary h-full"
          data-testid="waiting-tip-progress"
          style={{
            width: `${progress}%`,
            transition: progress ? `width ${TIP_ROTATION_MS}ms linear` : 'none',
          }}
        />
      </div>
    </div>
  );
}

function WaitingPanel() {
  // The pool name + Exit control live in the pool bar above the card now; this
  // panel is just the calm "waiting for the next call" content.
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
        <h2 className="text-xl font-semibold" data-testid="waiting-pool">
          Waiting for the next call
        </h2>
        <p className="text-muted-foreground">
          Stay on this screen — the next call connects automatically.
        </p>
        <a
          href={LEADFLOW_RESOURCES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary font-medium underline-offset-4 hover:underline"
        >
          Open call resources ↗
        </a>
        <RotatingTip />
      </CardContent>
    </Card>
  );
}

// The key that also performs the action, shown on the button so it is learned on the
// first call. Hidden from assistive tech so the button is still named "End call".
function KeyHint({ digit, className }: { digit: string; className?: string }) {
  return (
    <kbd
      aria-hidden
      className={cn(
        'ml-1 rounded border px-1 font-sans text-[10px] leading-4 opacity-70',
        className,
      )}
    >
      {digit}
    </kbd>
  );
}

function EndCallButton({
  disabled,
  onClick,
  testId,
}: {
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  // Deliberately NOT red and NOT in the Voicemail button's position (Inc 8): the
  // failure mode is an SDR reaching for the familiar spot out of habit and filing a
  // finished conversation as a voicemail. This is the "I finished a real conversation" end.
  return (
    <Button variant="secondary" disabled={disabled} onClick={onClick} data-testid={testId}>
      End call
      <KeyHint digit="0" />
    </Button>
  );
}

function DropCallButton({
  disabled,
  onClick,
  testId,
}: {
  disabled: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      variant="default"
      className="bg-red-600 text-white shadow-sm hover:bg-red-700"
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
    >
      Voicemail
      <KeyHint digit="1" className="border-white/40" />
    </Button>
  );
}

// Both end actions, rendered twice per card: once above the contact details and once
// below the talk track. The talk track is tall enough to push the lower row off a
// laptop screen, and hunting for it is the complaint this answers.
//
// `blockedReason` sits on the row, not on the buttons — a disabled button swallows
// pointer events, so a title there would never be seen.
function EndActions({
  disabled,
  blockedReason,
  source,
  onEndCall,
  onMarkVoicemail,
}: {
  disabled: boolean;
  blockedReason: string | null;
  source: 'top' | 'bottom';
  onEndCall: (source: HangupSource) => void;
  onMarkVoicemail: (source: HangupSource) => void;
}) {
  return (
    <div
      className="flex items-center gap-3"
      title={blockedReason ?? undefined}
      data-testid={`end-actions-${source}`}
    >
      <EndCallButton
        disabled={disabled}
        onClick={() => onEndCall(source)}
        testId={`end-call-${source}`}
      />
      <DropCallButton
        disabled={disabled}
        onClick={() => onMarkVoicemail(source)}
        testId={`mark-voicemail-${source}`}
      />
    </div>
  );
}

// Ticks only while a cut-mode prompt is up — a 250ms interval on one small span,
// torn down with the banner.
function SilenceCountdown({ deadlineAt }: { deadlineAt: number }) {
  const [leftMs, setLeftMs] = useState(() => Math.max(0, deadlineAt - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setLeftMs(Math.max(0, deadlineAt - Date.now())), 250);
    return () => clearInterval(id);
  }, [deadlineAt]);
  return (
    <span data-testid="silence-countdown">
      Ending call and leaving the pool in {Math.ceil(leftMs / 1000)}s…
    </span>
  );
}

// The mutual-silence gate's prompt (cockpit/silence-gate.ts): neither side has made
// a sound for the configured window. Continue is the big primary action — a wrongly
// raised prompt must cost exactly one obvious click. Voicemail / End call invoke the
// SAME handlers as the card's buttons (source 'gate'), so a disposition made from
// the banner is indistinguishable from one made from the card. The countdown
// renders only when armed (cut mode): a countdown that expires with no consequence
// would teach the floor that it bluffs.
//
// Sticky, because the call screen is long enough that reading a talk track scrolls this
// off. `z-30` sits under the fixed softphone box so mute stays reachable while it is up.
function SilenceGateBanner({
  deadlineAt,
  leadOnly,
  disabled,
  onStay,
  onMarkVoicemail,
  onEndCall,
}: {
  deadlineAt: number | null;
  // Which tier fired: the lead-only tier is worded as lead inactivity (the SDR
  // may well be audible — that's the tier's whole point).
  leadOnly: boolean;
  disabled: boolean;
  onStay: () => void;
  onMarkVoicemail: (source: HangupSource) => void;
  onEndCall: (source: HangupSource) => void;
}) {
  const stayRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // A stray keypress lands on Continue, which is the safe direction: it keeps the call.
  useFocusOnMount(stayRef, rootRef);

  return (
    <div ref={rootRef} className="sticky top-0 z-30 -mx-6 px-6 pt-2">
      <div
        className="rounded-md border-2 border-orange-400 bg-orange-50 px-3 py-3 text-sm text-orange-900 shadow-lg"
        data-testid="silence-gate-banner"
        role="alertdialog"
        aria-modal="false"
        aria-labelledby="silence-gate-title"
      >
        <div id="silence-gate-title" className="font-medium">
          {leadOnly
            ? 'Still on this call? No response from the lead.'
            : 'Still on this call? No sound from either side.'}
        </div>
        {deadlineAt != null && (
          <div className="mt-0.5 font-semibold text-orange-700">
            <SilenceCountdown deadlineAt={deadlineAt} />
          </div>
        )}
        <div className="mt-2 flex items-center gap-3">
          <Button
            ref={stayRef}
            size="lg"
            className="px-10 font-semibold"
            onClick={onStay}
            data-testid="silence-stay"
          >
            Continue
          </Button>
          <Button
            className="bg-red-600 text-white hover:bg-red-700"
            disabled={disabled}
            onClick={() => onMarkVoicemail('gate')}
            data-testid="silence-voicemail"
          >
            Voicemail
          </Button>
          <Button
            variant="secondary"
            disabled={disabled}
            onClick={() => onEndCall('gate')}
            data-testid="silence-end-call"
          >
            End call
          </Button>
        </div>
      </div>
    </div>
  );
}

function CallPanel({
  call,
  talkTrack,
  leadCalendar,
  ending,
  awaitingRemote,
  humanUnconfirmed,
  silencePrompt,
  onStaySilenceGate,
  online,
  micSilent,
  emailCheck,
  onMarkVoicemail,
  onEndCall,
}: {
  call: LeadCall;
  // The connected lead's client talk track, arriving after the card (null until
  // then / if none). Rendered inline below the contact details.
  talkTrack: CockpitState['talkTrack'];
  // The owning seller's calendar, behind the second tab. Null while in flight,
  // and null forever when nothing sends one — which is why a tab with nothing
  // behind it is dead rather than spinning.
  leadCalendar: CockpitState['leadCalendar'];
  ending: boolean;
  // True until the far side first speaks — the call is bridged but we've heard no
  // audio yet, so the label reads "Connecting to …". Flips to "Call connected" the
  // moment they talk.
  awaitingRemote: boolean;
  // Show the passive "couldn't confirm a human" banner (see useTwilioPhone).
  humanUnconfirmed: boolean;
  // The silence gate's prompt (null while down). deadlineAt non-null = cut mode.
  silencePrompt: { deadlineAt: number | null; rule: 'mutual' | 'lead_only' } | null;
  // The prompt's Continue button — stand the gate down for the rest of this call.
  onStaySilenceGate: () => void;
  // Whether the BFF WebSocket is connected. Either end action can only be sent over an open
  // link, so when offline the button is disabled and a notice explains why
  // (otherwise a click is silently dropped and the SDR is stuck on the card).
  online: boolean;
  // Nothing at all is reaching the browser from the SDR's microphone.
  micSilent: boolean;
  emailCheck: boolean;
  // The SDR pressed "Voicemail" — ends the current call, filing it as a
  // machine answer, and returns to waiting. The BFF counts it as an AMD miss.
  onMarkVoicemail: (source: HangupSource) => void;
  // The SDR finished a real conversation — ends the call as THEM (sdr_hangup),
  // never as a voicemail.
  onEndCall: (source: HangupSource) => void;
}) {
  // The card is on screen. Reported from HERE, not from the hook that receives the
  // lead frame: this component only renders when `phase` is on_call/ending AND
  // `currentCall` is set, and those two demonstrably drift (the resync effect below
  // exists for that case). An effect fires after the DOM commit, so this is the one
  // place that means "the SDR can see who they are talking to".
  //
  // Differencing it against the core's `call_events.carded` gives the rate at which a
  // card is sent and never lands; differencing it against `cockpit.blip.unmuted`
  // (reason `card`) isolates a lead frame that arrived but never painted.
  const cardLoggedRef = useRef<string | null>(null);
  useEffect(() => {
    const context = {
      call_id: call.callId,
      pool_id: call.poolId,
      // A guessed card (async AMD has not confirmed a human yet) still counts as
      // shown — the SDR can read it either way.
      confirmed: call.confirmed === true,
    };
    rumAction('cockpit.card.rendered', context);
    // Log twin: differencing "sent" against "seen" is the point of this event, and the
    // sent side lives in ClickHouse — a RUM action cannot be queried beside it, nor
    // alerted on (lib/rum.ts).
    //
    // ONCE PER CALL, unlike the action above. `confirmed` is a dependency because a
    // guessed card is upgraded when the verdict lands, and that re-render is worth a
    // second replay marker — but it is the same card being seen once. Logging it again
    // would inflate the "seen" side and mask genuinely missing cards, which is the one
    // thing this event exists to count.
    if (cardLoggedRef.current !== call.callId) {
      cardLoggedRef.current = call.callId;
      logCall('card_rendered', { event: 'cockpit_card_rendered', ...context });
    }
  }, [call.callId, call.poolId, call.confirmed]);

  // Guarded here rather than by a boundary, because no boundary can reach it:
  // this runs in the body of the component that renders them, so a RangeError
  // on a zone ICU does not know would unwind past the whole card and take the
  // call with it. The zone arrives from a lead record and is not validated.
  const leadTimestamp = useMemo(() => {
    try {
      return new Date().toLocaleTimeString('en-US', { timeZone: call.timezone });
    } catch {
      return null;
    }
  }, [call.timezone]);

  // Keyed on the call so a new one always opens on the lead. Picking up a fresh
  // human while looking at the last one's calendar would be its own small
  // disaster.
  const [tab, setTab] = useState<'lead' | 'calendar'>('lead');
  useEffect(() => {
    setTab('lead');
  }, [call.callId]);

  const calendarReady = leadCalendar?.status === 'ready';
  const calendarTabDisabled = leadCalendar === null || leadCalendar.status === 'unsupported';
  // Falling back to the lead tab matters when a card is replaced mid-look: the
  // next call may have no calendar at all.
  const activeTab = calendarTabDisabled ? 'lead' : tab;

  // Derived once and shared by both rows so the two can never disagree about whether
  // the call can be ended — and so the keyboard path, which is gated on the same two
  // facts, always matches what the SDR sees.
  const endBlocked = ending || !online;
  const blockedReason = ending
    ? 'Ending call…'
    : !online
      ? 'Reconnecting to the server — you’ll be able to end the call once the link is back.'
      : null;

  return (
    <div className="space-y-3">
      {silencePrompt != null && (
        <SilenceGateBanner
          // Remount per prompt, so a second raise re-runs the mount-only focus effect.
          key={silencePrompt.deadlineAt ?? 'unarmed'}
          deadlineAt={silencePrompt.deadlineAt}
          leadOnly={silencePrompt.rule === 'lead_only'}
          disabled={endBlocked}
          onStay={onStaySilenceGate}
          onMarkVoicemail={onMarkVoicemail}
          onEndCall={onEndCall}
        />
      )}
      {humanUnconfirmed && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          data-testid="human-unconfirmed-banner"
          role="status"
        >
          We couldn't confirm a human on this call — if a machine answered, use Voicemail.
        </div>
      )}
      <Card data-testid="call-screen">
        <CardHeader>
          {awaitingRemote ? (
            <CardDescription
              className="tracking-wide text-amber-600 uppercase"
              data-testid="call-status"
            >
              Connecting to {call.contactName}…
            </CardDescription>
          ) : (
            <CardDescription
              className="tracking-wide text-emerald-600 uppercase"
              data-testid="call-status"
            >
              Call connected
            </CardDescription>
          )}
          {call.origin === 'inbound' && (
            <div className="mt-1" data-testid="call-origin">
              {/* They rang us: the one fact that changes how an SDR opens the
                  call, so it cannot be missable. */}
              <span className="rounded bg-amber-500 px-2 py-0.5 text-xs font-bold tracking-wide text-white uppercase">
                Inbound — they called you
              </span>
            </div>
          )}
          <CardTitle className="text-xl">{call.contactName}</CardTitle>
          <CardDescription>
            {call.title ? `${call.title} · ` : ''}
            {call.company}
          </CardDescription>
          {call.clientName && (
            <div className="mt-2 flex items-center gap-2" data-testid="call-client">
              <span className="rounded bg-indigo-600 px-2 py-0.5 text-xs font-bold tracking-wide text-white uppercase">
                Client
              </span>
              <span className="text-base font-semibold">{call.clientName}</span>
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {micSilent && (
            <div
              className="border-destructive/40 bg-destructive/5 text-destructive flex items-start gap-3 rounded-md border px-4 py-3 text-sm"
              data-testid="call-mic-silent-banner"
              role="alert"
            >
              <MicOff className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">We're not picking up your microphone.</div>
                <p className="mt-1">
                  If you're speaking, the lead can't hear you. Check your headset's mute switch and
                  the microphone selected in the top-left panel.
                </p>
              </div>
            </div>
          )}
          <Tabs
            value={activeTab}
            onValueChange={(id) => setTab(id === 'calendar' ? 'calendar' : 'lead')}
          >
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <EndActions
                disabled={endBlocked}
                blockedReason={blockedReason}
                source="top"
                onEndCall={onEndCall}
                onMarkVoicemail={onMarkVoicemail}
              />
              <TabsList aria-label="Lead details or seller calendar">
                <TabsTrigger value="lead" data-testid="tab-lead">
                  <User className="h-4 w-4" />
                  Lead
                  {/* Both triggers reserve a badge slot, so the count arriving
                      mid-call cannot resize the strip. */}
                  <span className="w-8 text-right text-xs tabular-nums opacity-70" />
                </TabsTrigger>
                <TabsTrigger
                  value="calendar"
                  disabled={calendarTabDisabled}
                  data-testid="tab-calendar"
                >
                  <CalendarDays className="h-4 w-4" />
                  Calendar
                  <span
                    className="w-8 text-right text-xs tabular-nums opacity-70"
                    data-testid="tab-calendar-badge"
                  >
                    {calendarReady ? (slotCountBadge(leadCalendar.calendar) ?? '') : ''}
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>
            {!calendarTabDisabled && (
              // Mounted while hidden: an SDR who sets a timezone, glances at the
              // lead's persona and comes back must not lose the clock they were
              // reading times on.
              <TabsContent value="calendar" forceMount hidden={activeTab !== 'calendar'}>
                {/* Scoped to the calendar and no wider: it formats zone names it
                    did not produce, and Intl throws on one it does not recognise.
                    Wider would swallow failures that ought to be loud, and wider
                    still would take the controls that end the call. Keyed per call
                    so one bad timezone does not outlast the lead that carried it. */}
                <Boundary
                  where="region:cockpit-calendar"
                  resetKey={call.callId}
                  fallback={() => <CalendarUnavailable />}
                >
                  <SellerCalendar
                    status={leadCalendar?.status ?? 'unavailable'}
                    calendar={leadCalendar?.calendar ?? null}
                    leadTimeZone={call.timezone}
                  />
                </Boundary>
              </TabsContent>
            )}
            <TabsContent value="lead" className="space-y-2">
              {/* The lead's own fields, formatted from whatever the record
                  holds. Scoped to the list so a bad value costs the details
                  rather than the card they sit in. */}
              <Boundary where="region:cockpit-lead-details" resetKey={call.callId} size="panel">
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
                  <dt className="text-muted-foreground">Phone</dt>
                  <dd>
                    <a href={`tel:${call.phone}`} className="text-primary">
                      {call.phone}
                    </a>
                  </dd>
                  {call.persona && (
                    <>
                      <dt className="text-muted-foreground">Persona</dt>
                      <dd data-testid="call-persona">{call.persona}</dd>
                    </>
                  )}
                  {call.location && (
                    <>
                      <dt className="text-muted-foreground">Location</dt>
                      <dd data-testid="call-location">{call.location}</dd>
                    </>
                  )}
                  <dt className="text-muted-foreground">Timezone</dt>
                  <dd>
                    {/* The zone still shows when its clock could not be read:
                        an SDR can judge callable hours from the name alone. */}
                    {leadTimestamp == null
                      ? `(${call.timezone})`
                      : `${leadTimestamp} (${call.timezone})`}
                  </dd>
                  {call.email && (
                    <>
                      <dt className="text-muted-foreground">Email</dt>
                      <dd>
                        <a
                          href={`mailto:${call.email}`}
                          className="text-primary"
                          data-testid="call-email"
                        >
                          {call.email}
                        </a>
                      </dd>
                    </>
                  )}
                  {call.linkedinUrl && (
                    <>
                      <dt className="text-muted-foreground">LinkedIn</dt>
                      <dd>
                        <a
                          href={call.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary font-medium underline-offset-4 hover:underline"
                          data-testid="call-linkedin"
                        >
                          View profile ↗
                        </a>
                      </dd>
                    </>
                  )}
                </dl>
              </Boundary>
              <div className="text-sm">
                <span className="text-muted-foreground">Resources: </span>
                <a
                  href={LEADFLOW_RESOURCES_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-medium underline-offset-4 hover:underline"
                  data-testid="call-resources"
                >
                  Open call resources ↗
                </a>
              </div>
              {/* Renders markdown we did not write, so the failure is someone
                  else's content rather than our code. Inside the tab and no
                  wider: the disposition row sits in this same card. */}
              <Boundary where="region:cockpit-talk-track" resetKey={call.callId} size="panel">
                <TalkTrackPanel
                  name={talkTrack?.name ?? null}
                  markdown={talkTrack?.markdown ?? null}
                  throxyClientId={call.throxyClientId}
                />
              </Boundary>
            </TabsContent>
          </Tabs>
          {/* Outside the tabs: an address is read out whichever one is open. */}
          {/* Masked at the wrapper, not just inside: the panel below masks its
              own fields, but a fallback rendered in their place is outside that
              subtree and would put a thrown message -- which can carry the
              address just typed -- into a replay unmasked. */}
          <div data-dd-privacy="mask">
            <Boundary where="region:cockpit-email-check" resetKey={call.callId} size="inline">
              <EmailCheck enabled={emailCheck} callKey={call.callId} surface="cockpit" />
            </Boundary>
          </div>
          {/* Two end actions, two intents (Inc 8): "End call" finished a real
            conversation (ended_by: sdr); "Voicemail" files a machine answer.
            Both stop the call and return you to waiting. Block while ending OR
            while the link is down (a frame sent over a closed WS is silently
            dropped, leaving the SDR stuck here). */}
          <div className="border-t pt-4">
            <EndActions
              disabled={endBlocked}
              blockedReason={blockedReason}
              source="bottom"
              onEndCall={onEndCall}
              onMarkVoicemail={onMarkVoicemail}
            />
            {/* One sentence, shared with the row's hover text above, so the two can
                never drift apart. */}
            {blockedReason && (
              <p
                className={cn('mt-3 text-sm', ending ? 'text-muted-foreground' : 'text-amber-600')}
                data-testid={ending ? 'ending-note' : 'disposition-offline-note'}
              >
                {blockedReason}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CockpitBody({
  state,
  awaitingRemote,
  humanUnconfirmed,
  silencePrompt,
  onStaySilenceGate,
  online,
  micBlocked,
  micSilent,
  phone,
  emailCheck,
  selectPool,
  markVoicemail,
  endCall,
}: {
  state: CockpitState;
  awaitingRemote: boolean;
  humanUnconfirmed: boolean;
  silencePrompt: { deadlineAt: number | null; rule: 'mutual' | 'lead_only' } | null;
  onStaySilenceGate: () => void;
  online: boolean;
  micBlocked: boolean;
  micSilent: boolean;
  phone: MicCheckPhone;
  emailCheck: boolean;
  selectPool: (poolId: string) => void;
  markVoicemail: (source: HangupSource) => void;
  endCall: (source: HangupSource) => void;
}) {
  switch (state.phase) {
    case 'connecting':
    case 'signed_out':
      return <p className="text-muted-foreground text-sm">Connecting…</p>;
    case 'pool_select':
    case 'joining':
      // Both of these render between calls, never during one, so a failure here
      // costs a screen rather than a conversation. They sit beside the call
      // panel in this switch, never above it.
      return (
        <Boundary
          where="region:cockpit-pool-picker"
          // Keyed on the pools, not the phase: the phase stays `pool_select`
          // while frames keep arriving, so a throw on one bad snapshot would
          // never clear and the SDR could join nothing until they reloaded.
          resetKey={state.pools.map((p) => `${p.id}:${p.status}:${p.waiting}`).join('|')}
          size="page"
        >
          <PoolPicker
            pools={state.pools}
            busy={state.phase === 'joining'}
            pendingName={poolName(state, state.pendingPoolId)}
            micBlocked={micBlocked}
            phone={phone}
            exitReason={state.exitReason}
            error={state.error}
            onSelect={selectPool}
          />
        </Boundary>
      );
    case 'waiting':
      return (
        <Boundary where="region:cockpit-waiting" resetKey={state.phase} size="page">
          <WaitingPanel />
        </Boundary>
      );
    case 'on_call':
    case 'ending':
      return state.currentCall ? (
        <CallPanel
          call={state.currentCall}
          talkTrack={state.talkTrack}
          leadCalendar={state.leadCalendar}
          ending={state.phase === 'ending'}
          awaitingRemote={awaitingRemote}
          humanUnconfirmed={humanUnconfirmed}
          silencePrompt={silencePrompt}
          onStaySilenceGate={onStaySilenceGate}
          online={online}
          micSilent={micSilent}
          emailCheck={emailCheck}
          onMarkVoicemail={markVoicemail}
          onEndCall={endCall}
        />
      ) : null;
    default:
      return null;
  }
}

// Dev-only control to switch which SDR this tab acts as. Typing a name + Switch sets
// `?sdr=<name>` and reloads, so the WS reconnects as that (auto-created) SDR. Open
// more tabs with different names to run several SDRs in one pool at once. Rendered
// only under `import.meta.env.DEV`, so it never ships to prod.
function ImpersonationSwitcher({ current }: { current: string | null }) {
  const [name, setName] = useState(current ?? '');
  return (
    <div
      className="flex items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-2 py-1"
      data-testid="impersonate-switcher"
    >
      <span className="text-[10px] font-semibold tracking-wide text-amber-700 uppercase">
        Dev · acting as
      </span>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') setImpersonateSdr(name);
        }}
        placeholder={DEV_SDR_PLACEHOLDER}
        className="h-7 w-36 rounded border bg-white px-2 text-sm"
        data-testid="impersonate-input"
        aria-label="Impersonate SDR"
      />
      <Button size="sm" variant="secondary" onClick={() => setImpersonateSdr(name)}>
        Switch
      </Button>
    </div>
  );
}

// Reconcile cadence (see the effect in Cockpit): poll every 2s while audio is up but no
// card has shown, give up after 30s.
const RECONCILE_INTERVAL_MS = 2000;
const RECONCILE_CAP_MS = 30_000;

// Cadence of the background cockpit.link_parity sample while both link-health ledgers
// run (single-websocket plan). A window that arrives at >1.5× this is a throttled/
// suspended tab and is stamped `suspended` for the analysis to filter.
const PARITY_INTERVAL_MS = 60_000;

function Cockpit() {
  const { userEmail } = cockpitRoute.useRouteContext();
  const impersonateSdr = getImpersonateSdr();

  const {
    state,
    wsStatus,
    linkHealth,
    singleWs,
    emailCheck,
    selectPool,
    leavePool,
    abandonLeave,
    refreshVoiceToken,
    reportVoicemailPromptRaised,
    reportSilenceGate,
    reportClientError,
    markVoicemail,
    endCall,
    resyncCall,
    reportActivity,
    answerPresence,
  } = useCockpit(impersonateSdr);
  // A disposition (and any other action) can only be sent over an open BFF link.
  const online = wsStatus === 'open';
  // When this tab last lost its seat in a pool, feeding the inbound guard
  // (cockpit/inbound-guard.ts). Refs, not state: they are read at ring time inside the
  // Twilio device handler and must never re-render the cockpit. Both are written in
  // ONE effect so they can never disagree about the same phase change, and never during
  // render — a render that is started and discarded would leave the ref set.
  const unseatedSince = useRef<number | null>(null);
  const phaseRef = useRef(state.phase);
  useEffect(() => {
    phaseRef.current = state.phase;
    if (state.phase === 'pool_select') {
      // `??=`, not `=`: re-entering the picker after a reconnect must not restart the
      // clock, or a flapping socket keeps a forgotten tab permanently answerable.
      unseatedSince.current ??= Date.now();
    } else if (state.phase !== 'connecting' && state.phase !== 'signed_out') {
      unseatedSince.current = null; // seated
    }
    // `connecting`/`signed_out` deliberately pass through: a socket blip must neither
    // seat an idle tab nor un-seat a real one.
  }, [state.phase]);
  const decideInboundNow = useCallback(
    () =>
      decideInbound({
        phase: phaseRef.current,
        unseatedSince: unseatedSince.current,
        now: Date.now(),
      }),
    [],
  );

  // 0 ends the call, 1 files it as a voicemail — the same two actions as the buttons,
  // for SDRs who end a call every couple of minutes. Bound on the window because the
  // card holds no focus of its own.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const action = resolveHangupShortcut({
        key: event.key,
        code: event.code,
        repeat: event.repeat,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        altKey: event.altKey,
        editable: isEditableTarget(event.target),
        modalOpen: document.querySelector('dialog[open]') != null,
        phase: state.phase,
        online,
      });
      if (!action) return;
      event.preventDefault();
      if (action === 'end') endCall('key');
      else markVoicemail('key');
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [state.phase, online, endCall, markVoicemail]);

  // What a person at the keyboard looks like. Deliberately NOT visibility or focus — a
  // tab left open on a monitor is visible all night, and counting that would answer "is
  // this tab up", not "is anybody here".
  useEffect(() => {
    const events = ['pointermove', 'keydown', 'scroll', 'click'] as const;
    // Capture, because `scroll` does not bubble: without it, scrolling the talk-track
    // panel — which is most of what reading a lead looks like — counts as nobody home.
    const opts = { passive: true, capture: true };

    for (const e of events) window.addEventListener(e, reportActivity, opts);
    return () => {
      for (const e of events) window.removeEventListener(e, reportActivity, opts);
    };
  }, [reportActivity]);

  // Sound it once per prompt, and never while a card is up: on speakerphone the chime
  // goes out through the SDR's own mic and the lead hears it. Keyed on the card rather
  // than the phase because the audio outlives the SDR pressing End Call — the hangup
  // is driven by the phase effect below, so there is a window where the lead is still
  // connected. Mid-call the overlay is the whole prompt.
  const chimedForRef = useRef<string | null>(null);
  const presenceWarning = state.presenceWarning;
  const callUp = state.currentCall != null;
  useEffect(() => {
    if (presenceWarning == null) {
      chimedForRef.current = null;
      return;
    }
    // Stamped only once a chime is actually attempted, so a prompt raised mid-call
    // still sounds the moment the call ends rather than being marked done in silence.
    if (callUp || chimedForRef.current === presenceWarning.expiresAt) return;
    chimedForRef.current = presenceWarning.expiresAt;
    playPresenceChime();
  }, [presenceWarning, callUp]);

  // The Voice token is pushed by the BFF over the WS (state.voiceToken); the
  // softphone consumes it and asks for a refresh over the WS on expiry.
  const twilio = useTwilioPhone({
    enabled: true,
    voiceToken: state.voiceToken,
    onRefreshNeeded: refreshVoiceToken,
    onVoicemailPromptRaised: reportVoicemailPromptRaised,
    // Every gate receipt goes to the BFF; a countdown-expiry cut ALSO ends the
    // call — through the normal endCall path, so ended_by stays sdr_hangup and
    // the silence_gate_cut row is what marks it. Report first: reportSilenceGate
    // is phase-guarded and endCall moves the phase.
    onSilenceGate: (event) => {
      reportSilenceGate(event);
      if (event.action === 'cut') endCall('gate');
    },
    onClientError: reportClientError,
    onLocalAudible: reportActivity,
    currentCall: state.currentCall,
    decideInbound: decideInboundNow,
    // Gates the self-healing rebuild escalation (not the free recovery arms):
    // only a seat that can actually receive calls spends the rebuild budget.
    seated: state.activePoolId != null,
  });
  // A backgrounded tab has nothing but its own name to go on.
  useTitleAlert(twilio.silencePrompt != null, 'Call requires input');
  // Warns (with a one-click switch) when they're dialing on the laptop mic with
  // a headset attached. Renders nothing here — it drives the app-level toaster.
  useIdleMicToast(twilio);
  // Emitted HERE rather than inside useTwilioPhone, which has no pool knowledge:
  // whether the SDR is in a pool is what separates a nuisance from taking live calls
  // nobody can hear them on. The suppression rules live in micEpisode, where they can
  // be tested.
  const micBlockedSince = useRef<number | null>(null);
  useEffect(() => {
    const inPool = state.activePoolId != null;
    const { blockedSince, report, blockedMs } = micEpisode(
      micBlockedSince.current,
      twilio.micState,
      Date.now(),
    );
    micBlockedSince.current = blockedSince;
    if (report === 'blocked') {
      reportMicBlocked({
        permission: twilio.micPermission,
        error_message: twilio.micError,
        in_pool: inPool,
        retried: twilio.micRetried,
      });
    } else if (report === 'recovered') {
      reportMicRecovered({ blocked_ms: blockedMs ?? 0, in_pool: inPool });
    }
  }, [
    twilio.micState,
    twilio.micPermission,
    twilio.micError,
    twilio.micRetried,
    state.activePoolId,
  ]);
  // Duplicate cockpit tabs split a call — one tab wins the Twilio accept race and holds
  // the audio, the other gets the card — so ask the SDR to close the extras. Scoped to
  // this SDR: dev impersonation opens tabs as different people deliberately.
  const openTabs = useOpenTabCount(impersonateSdr ?? userEmail);
  // The manual dialer open in another tab holds this SDR's manual phone — and the seat
  // lock with it, so every join here is refused until it closes. Listened to, never
  // announced on: this tab is not a manual tab.
  const manualTabs =
    useOpenTabCount(impersonateSdr ?? userEmail, {
      channel: MANUAL_TABS_CHANNEL,
      announce: false,
    }) - 1;
  // Marks the session replay at the moment a duplicate appears, so a support person
  // watching a "no audio" recording can see whether this was in play.
  useEffect(() => {
    if (openTabs > 1) rumAction('cockpit.tabs.multiple', { tab_count: openTabs });
  }, [openTabs]);
  // True once the deploy has published a bundle newer than the one this tab is running.
  // A tab open across a deploy keeps its old code indefinitely, so every client-side fix
  // reaches the forgotten tab last — this is what lets the SDR find out without being told.
  const { showBanner: staleBundle } = useStaleBundle();
  // BFF link health for the control box, alive for the whole session. Under the
  // single-websocket flag (server-decided, via the hello) the dedicated ping
  // socket stays shut and the chip reads the health measured on the cockpit
  // socket itself — the one that carries calls (plans/single-websocket/plan.md).
  // Strictly `=== false`: the probe opens only once a hello has SAID two-socket
  // mode (legacy hellos read as false) — never on the pre-hello null, where a
  // flag-ON tab would pointlessly mint a session it then abandons.
  const bffHealth = useBffPing(impersonateSdr, { enabled: singleWs === false });
  const chipHealth = singleWs === true ? linkHealth : bffHealth;

  // Background comparison while both ledgers run (flag off): ~1/min, so the flip to
  // single-websocket is decided from fleet data, not assumption. Latest readings
  // via a ref — both objects refresh at 1Hz and an interval closure would freeze
  // the first render's values.
  const parityRef = useRef({ inBand: linkHealth, socket: bffHealth });
  parityRef.current = { inBand: linkHealth, socket: bffHealth };
  // Counters are published as PER-WINDOW DELTAS against this baseline, not
  // cumulative totals: the two ledgers deliberately diverge across a browser
  // suspension (the in-band ledger forgives throttled pings, the probe hook
  // counts them — Codex P2 on #668), and cumulative totals would carry that one
  // window's disagreement for the rest of the mount. A window that ran long is
  // stamped `suspended` so analysis filters it instead of reading it as drift.
  const parityBaseRef = useRef<{
    at: number;
    inBandMiss: number;
    socketMiss: number;
    inBandPong: number;
    socketPong: number;
  } | null>(null);
  useEffect(() => {
    if (singleWs !== false) return; // one ledger (or no hello yet) — nothing to compare
    parityBaseRef.current = null; // fresh baseline whenever the comparison (re)starts
    const id = setInterval(() => {
      const { inBand, socket } = parityRef.current;
      const now = Date.now();
      const base = parityBaseRef.current;
      parityBaseRef.current = {
        at: now,
        inBandMiss: inBand.missCount,
        socketMiss: socket.missCount,
        inBandPong: inBand.pongCount,
        socketPong: socket.pongCount,
      };
      if (!base) return; // first tick establishes the baseline only
      const windowMs = now - base.at;
      rumAction('cockpit.link_parity', {
        window_ms: windowMs,
        suspended: windowMs > PARITY_INTERVAL_MS * 1.5,
        in_band_status: inBand.status,
        socket_status: socket.status,
        in_band_quality: inBand.quality,
        socket_quality: socket.quality,
        quality_agrees: inBand.quality === socket.quality,
        in_band_rtt: inBand.rtt,
        socket_rtt: socket.rtt,
        rtt_delta: inBand.rtt !== null && socket.rtt !== null ? inBand.rtt - socket.rtt : null,
        in_band_jitter: Math.round(inBand.jitterMs),
        socket_jitter: Math.round(socket.jitterMs),
        in_band_misses: inBand.missCount - base.inBandMiss,
        socket_misses: socket.missCount - base.socketMiss,
        in_band_pongs: inBand.pongCount - base.inBandPong,
        socket_pongs: socket.pongCount - base.socketPong,
      });
    }, PARITY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [singleWs]);

  // The one real telephony side effect: drive the Twilio call off the cockpit
  // phase — accept on on_call, hang up otherwise.
  useEffect(() => {
    if (state.phase === 'on_call') twilio.startCall();
    else twilio.endCall();
  }, [state.phase, twilio.startCall, twilio.endCall]);

  // Couple audio-connect → call panel: if Twilio is `on_call` but no card arrived
  // (`currentCall == null`), the core→BFF push was lost or late — poll the BFF to re-push
  // it. Self-terminates when the card lands or audio drops; the 30s cap bounds a genuine
  // voicemail (which never shows a card). The deps stay stable through the limbo window,
  // so the interval is created once and cleared once — do NOT add churny Twilio fields
  // (quality/deviceRtt) or it resets sub-2s and never fires.
  useEffect(() => {
    if (twilio.status !== 'on_call' || state.currentCall != null) return;
    const startedAt = Date.now();
    const id = setInterval(() => {
      if (Date.now() - startedAt > RECONCILE_CAP_MS) {
        clearInterval(id);
        return;
      }
      resyncCall();
    }, RECONCILE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [twilio.status, state.currentCall, resyncCall]);

  const onSignOut = () => {
    stopRum(); // stop recording before we tear down / navigate away
    // Local dev: no real auth endpoint — set the signed-out flag and go to /login
    // (the route guard then keeps us there). Unmounting closes the WS, so the BFF
    // also drops us from the pool on the core.
    if (DEV_AUTH_BYPASS) {
      devSignOut();
      window.location.href = '/login';
      return;
    }
    void authClient.signOut({
      fetchOptions: {
        onSuccess: () => {
          window.location.href = '/login';
        },
      },
    });
  };

  // The pool the SDR is currently in (waiting / on a call / ending). The big pool
  // header + Exit control sit above the card; leaving works mid-call too (the
  // reducer clears the call and the Twilio hangup follows the phase change).
  const activePoolName = poolName(state, state.activePoolId);
  const inPool = state.activePoolId != null;
  const activePool = state.pools.find((p) => p.id === state.activePoolId);
  const pauseBanner = poolPauseBanner(activePool, state.deploying);
  // Phases where a page reload would cut a live WebRTC leg. Derived once: the stale-bundle
  // banner uses it for both its copy and whether it offers a Refresh button, and those two
  // must never disagree about what is safe.
  const reloadWouldCutCall = state.phase === 'on_call' || state.phase === 'ending';
  // Any in-progress leave (awaiting the server's verdict, or draining pending calls)
  // locks the Exit button — the SDR has already asked to go.
  const leaving = state.leaveStatus !== 'none';

  // Age the pending leave so the overlay can escalate. Runs only while a leave is
  // pending, so it costs nothing in the normal case.
  const [leaveNow, setLeaveNow] = useState(() => Date.now());
  useEffect(() => {
    if (!leaving) return;
    setLeaveNow(Date.now());
    const tick = setInterval(() => setLeaveNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, [leaving]);
  const leaveIsStalled = leaveStalled(state.leavingSince, leaveNow);

  return (
    // A call lives in this page, so any error screen below here must not
    // offer to reload the document -- that would end it, and nothing asks
    // first. Set here rather than per boundary so one added later inherits
    // the rule instead of having to remember it.
    <ReloadGuard active={state.currentCall != null}>
      <>
        {/* Renders the device's state, does not own it, so the call survives its fallback. */}
        <Boundary
          where="region:cockpit-control-box"
          resetKey={state.currentCall?.callId ?? 'idle'}
          fallback={() => <ControlsUnavailable />}
        >
          {/* `joining` as well as in-pool: the leg can be routed the moment the join lands,
            and the inbound handler treats a joining tab as seated (cockpit/inbound-guard.ts). */}
          <TwilioControlBox
            twilio={twilio}
            bffHealth={chipHealth}
            seatedForCalls={inPool || state.phase === 'joining'}
          />
        </Boundary>
        <div className="mx-auto max-w-3xl p-6">
          {state.presenceWarning != null && (
            // The fallback carries a working confirm rather than an error screen.
            // This overlay holds the only control that keeps the seat, and the
            // countdown behind it runs whether or not the prompt drew -- so a
            // default fallback here would evict someone for a formatting bug.
            <Boundary
              where="region:cockpit-presence"
              resetKey={state.presenceWarning.expiresAt}
              fallback={() => <PresenceFallback online={online} onConfirm={answerPresence} />}
            >
              <PresenceOverlay
                trigger={state.presenceWarning.trigger}
                expiresAt={state.presenceWarning.expiresAt}
                online={online}
                onConfirm={answerPresence}
              />
            </Boundary>
          )}
          <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-lg font-semibold">Maestro · Dialer</h1>
              {impersonateSdr ? (
                <p className="text-sm">
                  <span className="font-medium text-amber-700">acting as {impersonateSdr}</span>
                  <span className="text-muted-foreground/70"> · {userEmail}</span>
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">{userEmail}</p>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {import.meta.env.DEV && <ImpersonationSwitcher current={impersonateSdr} />}
              <Button variant="outline" size="sm" onClick={onSignOut}>
                <LogOut />
                Sign out
              </Button>
            </div>
          </header>

          {/* Above the duplicate-tab warning, and not pool-gated: "close all but one tab"
            is useless advice to someone who cannot be heard in any of them. */}
          {twilio.micBanner && (
            <div
              className="border-destructive/40 bg-destructive/5 text-destructive mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm"
              data-testid="mic-blocked-banner"
              role="alert"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">{twilio.micBanner.title}</div>
                <p className="mt-1">{twilio.micBanner.body}</p>
              </div>
              <Button size="sm" variant="outline" onClick={twilio.recheckMic}>
                Re-check
              </Button>
            </div>
          )}

          {/* Duplicate tabs. Deliberately NOT gated on being in a pool — the dangerous tab is
            usually the forgotten one sitting on the picker, and it is just as likely to be
            the tab the SDR is reading this in. Red, not amber: unlike the other banners,
            this one is asking them to do something. */}
          {openTabs > 1 && (
            <div
              className="border-destructive/40 bg-destructive/5 text-destructive mb-5 flex items-start gap-3 rounded-md border px-4 py-3 text-sm"
              data-testid="multi-tab-banner"
              role="status"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">
                  You have {openTabs} dialer tabs open — please close all but one.
                </div>
                <p className="mt-1">
                  A second tab can pick up your call: you get the lead’s card, the other tab gets
                  the audio, and the lead hears nobody answer. Closing the extras fixes it — this
                  warning clears itself.
                </p>
              </div>
            </div>
          )}
          {manualTabs > 0 && (
            <div
              className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="manual-tab-banner"
              role="status"
            >
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <div className="font-medium">The manual dialer is open in another tab.</div>
                <p className="mt-1">
                  That tab holds your phone, so pools won’t take you here until it is closed.
                </p>
              </div>
            </div>
          )}

          {/* Out-of-date bundle. Amber, and BELOW the duplicate-tab warning: a tab that is both
            stale and duplicated should be told to close first, which needs no timing, whereas
            this one says "when you can". Not gated on being in a pool — the tab most worth
            refreshing is the forgotten one on the picker. */}
          {/* Suppressed while deploying: this banner tells the SDR to leave the pool and
            refresh, which is the worst instruction to follow mid-deploy. Nothing is
            lost — the deploy flag clears and this returns on the next version poll. */}
          {staleBundle && !state.deploying && (
            <div
              className="mb-5 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="stale-bundle-banner"
              role="status"
            >
              <RefreshCw className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="font-medium">A newer version of the dialer is available.</div>
                <p className="mt-1 text-amber-700">
                  {refreshHint(reloadWouldCutCall, inPool, state.seatHeld)}
                </p>
              </div>
              {/* Offered on every branch whose copy says a refresh is safe. Mid-call stays
                blocked either way: a reload tears down the Twilio Device and cuts live
                audio regardless of the seat. Idle-in-pool unlocks only when the server
                advertises seatHeld — without it the BFF's close path deregisters the SDR
                non-gracefully, so reloading while waiting hangs up dials already ringing
                out and the graceful route stays the Exit control. */}
              {!reloadWouldCutCall && (state.seatHeld || !inPool) && (
                <Button size="sm" variant="outline" onClick={() => window.location.reload()}>
                  Refresh
                </Button>
              )}
            </div>
          )}

          {/* Big pool name + a prominent Exit control above the card (all in-pool phases). */}
          {inPool && (
            <div className="mb-5 flex items-end justify-between gap-4" data-testid="pool-bar">
              <div>
                <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
                  Pool
                </div>
                <h2 className="text-3xl font-bold" data-testid="pool-heading">
                  {activePoolName}
                </h2>
              </div>
              <Button
                variant="outline"
                size="lg"
                disabled={leaving}
                onClick={leavePool}
                data-testid="leave-pool"
                title="Leave this pool"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <DoorOpen />
                {leaving ? 'Leaving…' : 'Exit pool'}
              </Button>
            </div>
          )}

          {/* Deploy drain banner: the core paused dialing while it finishes in-flight
            calls and the node is replaced. Advisory — the SDR keeps their pool and
            any live call; no new calls start until the cockpit reconnects. */}
          {state.deploying && inPool && (
            <div
              className="mb-5 flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="deploying-banner"
              role="status"
            >
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              <span>
                We&apos;re deploying an update. Your place in the pool is safe and dialling resumes
                automatically — no action needed from you.
              </span>
            </div>
          )}

          {/* Pool paused by an operator. A joined SDR making status === 'paused' is always
            an operator pause (a no-SDRs pause can't happen while they're in it), so this
            reuses run_state with no extra field. Takes precedence over off-hours. */}
          {pauseBanner === 'operator' && (
            <div
              className="mb-5 flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="operator-paused-banner"
              role="status"
            >
              <PauseCircle className="h-4 w-4 shrink-0" />
              <span>
                This pool was paused by an operator — no new calls will start until it’s resumed.
              </span>
            </div>
          )}

          {/* Off-hours: the pool is open and healthy, but every remaining lead is asleep.
            Explains the wait (and the timezone makeup) so the SDR knows nothing is broken. */}
          {pauseBanner === 'off-hours' && activePool?.offHours && (
            // Formats zone names and clock times it did not produce, and Intl
            // throws on a zone it does not recognise.
            <Boundary where="region:cockpit-off-hours" resetKey={activePool.id} size="inline">
              <OffHoursBanner offHours={activePool.offHours} />
            </Boundary>
          )}

          <CockpitBody
            state={state}
            awaitingRemote={twilio.awaitingRemote}
            humanUnconfirmed={twilio.humanUnconfirmed}
            silencePrompt={twilio.silencePrompt}
            onStaySilenceGate={twilio.staySilenceGate}
            online={online}
            micBlocked={twilio.micState === 'blocked'}
            micSilent={twilio.micSilence != null}
            phone={twilio}
            emailCheck={emailCheck}
            selectPool={selectPool}
            markVoicemail={markVoicemail}
            endCall={endCall}
          />
        </div>

        {/* Graceful exit: the SDR clicked Exit with calls still in flight, so the core
          is draining them (never cutting one off). We wait here and leave the pool
          automatically once they finish (the server's pool_left). Suppressed while a
          call is actually CONNECTED (a draining call reached a human) so the popup
          never covers the live card — the SDR works the call; the popup returns
          between calls. The "Leaving…" button keeps the leave context meanwhile. */}
        {/* Never covers a live card (`currentCall == null`). Before the stall threshold this
          is the original "please wait" popup and `requested` shows nothing; past it the
          copy stops promising a quick exit and offers the way out. */}
        {(state.leaveStatus === 'draining' || leaveIsStalled) && state.currentCall == null && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            data-testid="leave-wait-overlay"
          >
            <div
              className="bg-card flex w-[min(28rem,94vw)] flex-col gap-4 rounded-lg border p-6 shadow-xl"
              data-testid="leave-wait-modal"
            >
              <div className="flex items-center gap-3">
                <Loader2 className="text-muted-foreground h-5 w-5 animate-spin" />
                <h2 className="text-lg font-semibold">
                  {leaveIsStalled ? 'Still finishing your calls' : 'Finishing your calls'}
                </h2>
              </div>
              {leaveIsStalled ? (
                <>
                  <p className="text-muted-foreground text-sm">
                    This is taking longer than usual —{' '}
                    {formatWaitedFor(state.leavingSince, leaveNow)} so far. You don&rsquo;t have to
                    keep waiting.
                  </p>
                  <Button
                    variant="outline"
                    onClick={abandonLeave}
                    data-testid="leave-wait-back-to-pools"
                  >
                    <DoorOpen />
                    Back to pools
                  </Button>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Please wait for your pending calls to finish — it won&rsquo;t be long.
                  You&rsquo;ll leave the pool automatically as soon as they end.
                </p>
              )}
            </div>
          </div>
        )}
      </>
    </ReloadGuard>
  );
}

export const cockpitRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // No session → login. The returned context (userEmail) is read by the component.
  beforeLoad: async () => {
    const { data } = await getSession();
    if (!data) throw redirect({ to: '/login' });
    // Started here rather than on mount: an effect runs after the first render
    // commits, so a component that throws on its very first render does so
    // while collection is still off and reports nothing. That is the case that
    // matters most — a deploy breaking this route for the whole fleet would be
    // the one failure nothing recorded. Idempotent, so repeat navigation keeps
    // one attributed session; stopping stays on real deauth only.
    startRumForUser(data.user.email);
    return { userEmail: data.user.email };
  },
  component: Cockpit,
  // A throw reaching here has already cost the call — unwinding past this
  // component tears down the device holding the audio. So this decides only
  // what is on screen afterwards, not whether the call survives; the boundaries
  // inside the page decide that. No operator navigation.
  errorComponent: CallSurfaceError,
  onCatch: (error) => reportRenderError(error, 'route:cockpit'),
});
